import { useEffect, useId, useRef, useState } from "react";

// =====================================================================
// SVG キャッシュ層 (in-memory + localStorage)
// 同じ Mermaid コードは何度開いても 1 回しかレンダしない。
// dark/light で別キーにする。
// =====================================================================
const memCache = new Map<string, string>();
const STORAGE_PREFIX = "mermaid-svg-cache:v3:";
const STORAGE_MAX_BYTES = 1_000_000; // 1MB を超えたら一掃 (LRU は実装せずシンプルに)

function hashKey(code: string, dark: boolean): string {
  // 高速なフィンガープリント (SHA は不要、衝突率は実用上 OK)
  let h = 0x811c9dc5;
  for (let i = 0; i < code.length; i++) {
    h ^= code.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `${dark ? "d" : "l"}_${(h >>> 0).toString(36)}_${code.length}`;
}

function readCache(key: string): string | null {
  const m = memCache.get(key);
  if (m) return m;
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (v) memCache.set(key, v);
    return v;
  } catch {
    return null;
  }
}

function writeCache(key: string, svg: string): void {
  memCache.set(key, svg);
  if (typeof window === "undefined") return;
  try {
    // ストレージ枯渇しないよう、ある程度溜まったら全消し
    if (window.localStorage.length > 200) {
      const keys: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(STORAGE_PREFIX)) keys.push(k);
      }
      let total = 0;
      for (const k of keys) {
        total += (window.localStorage.getItem(k) ?? "").length;
      }
      if (total > STORAGE_MAX_BYTES) {
        for (const k of keys) window.localStorage.removeItem(k);
      }
    }
    window.localStorage.setItem(STORAGE_PREFIX + key, svg);
  } catch {
    // QuotaExceeded などは無視 (memCache だけで動く)
  }
}

// chotdekiru ブランドのコードエディタ風シンタックスパレットに揃える
// (Light: bg #F4F0EA / text #2B2B2A / primary #05B45B / accent #F15025 / blue #1D6FD3)
const LIGHT_THEME_VARS = {
  background: "#F4F0EA",
  primaryColor: "#05B45B",
  primaryTextColor: "#FFFFFF",
  primaryBorderColor: "#04994F",
  secondaryColor: "#1D6FD3",
  secondaryTextColor: "#FFFFFF",
  secondaryBorderColor: "#1A5FB8",
  tertiaryColor: "#F15025",
  tertiaryTextColor: "#FFFFFF",
  tertiaryBorderColor: "#D7461F",
  lineColor: "#7B6F62",
  textColor: "#2B2B2A",
  mainBkg: "#05B45B",
  nodeBorder: "#04994F",
  clusterBkg: "#FFFFFF",
  clusterBorder: "#8A7F73",
  defaultLinkColor: "#7B6F62",
  edgeLabelBackground: "#F4F0EA",
  // edge label テキスト色（HTTP/HTML 等の矢印ラベルが白で見えなくなる対策）
  labelColor: "#2B2B2A",
  // ER diagram
  attributeBackgroundColorOdd: "#FFFFFF",
  attributeBackgroundColorEven: "#F4F0EA",
  // sequence
  actorBkg: "#05B45B",
  actorBorder: "#04994F",
  actorTextColor: "#FFFFFF",
  actorLineColor: "#7B6F62",
  signalColor: "#2B2B2A",
  signalTextColor: "#2B2B2A",
  labelBoxBkgColor: "#1D6FD3",
  labelBoxBorderColor: "#1A5FB8",
  labelTextColor: "#FFFFFF",
  loopTextColor: "#2B2B2A",
  noteBorderColor: "#F2B705",
  noteBkgColor: "#FFF7D6",
  noteTextColor: "#2B2B2A",
  // mindmap (cScaleN がレベルごとのノード背景、cScaleLabelN がテキスト色)
  cScale0: "#05B45B",
  cScaleLabel0: "#FFFFFF",
  cScale1: "#1D6FD3",
  cScaleLabel1: "#FFFFFF",
  cScale2: "#F15025",
  cScaleLabel2: "#FFFFFF",
  cScale3: "#7C3AED",
  cScaleLabel3: "#FFFFFF",
  cScale4: "#0EA5A4",
  cScaleLabel4: "#FFFFFF",
  cScale5: "#D97706",
  cScaleLabel5: "#FFFFFF",
  cScale6: "#BE185D",
  cScaleLabel6: "#FFFFFF",
  cScale7: "#475569",
  cScaleLabel7: "#FFFFFF",
};

const DARK_THEME_VARS = {
  background: "#1E1E1E",
  primaryColor: "#05B45B",
  primaryTextColor: "#FFFFFF",
  primaryBorderColor: "#3DC2A0",
  secondaryColor: "#82AAFF",
  secondaryTextColor: "#1E1E1E",
  secondaryBorderColor: "#5588DD",
  tertiaryColor: "#F2946B",
  tertiaryTextColor: "#1E1E1E",
  tertiaryBorderColor: "#D7726B",
  lineColor: "#B5B0A5",
  textColor: "#D4D4D4",
  mainBkg: "#05B45B",
  nodeBorder: "#3DC2A0",
  clusterBkg: "#2A2A2A",
  clusterBorder: "#6A9955",
  defaultLinkColor: "#B5B0A5",
  edgeLabelBackground: "#1E1E1E",
  labelColor: "#D4D4D4",
  attributeBackgroundColorOdd: "#1E1E1E",
  attributeBackgroundColorEven: "#2A2A2A",
  actorBkg: "#05B45B",
  actorBorder: "#3DC2A0",
  actorTextColor: "#FFFFFF",
  actorLineColor: "#B5B0A5",
  signalColor: "#D4D4D4",
  signalTextColor: "#D4D4D4",
  labelBoxBkgColor: "#82AAFF",
  labelBoxBorderColor: "#5588DD",
  labelTextColor: "#1E1E1E",
  loopTextColor: "#D4D4D4",
  noteBorderColor: "#F2B705",
  noteBkgColor: "#3A2F00",
  noteTextColor: "#F2B705",
  // mindmap
  cScale0: "#05B45B",
  cScaleLabel0: "#FFFFFF",
  cScale1: "#82AAFF",
  cScaleLabel1: "#1E1E1E",
  cScale2: "#F2946B",
  cScaleLabel2: "#1E1E1E",
  cScale3: "#A78BFA",
  cScaleLabel3: "#1E1E1E",
  cScale4: "#5EEAD4",
  cScaleLabel4: "#1E1E1E",
  cScale5: "#FBBF24",
  cScaleLabel5: "#1E1E1E",
  cScale6: "#F472B6",
  cScaleLabel6: "#1E1E1E",
  cScale7: "#94A3B8",
  cScaleLabel7: "#1E1E1E",
};

function detectDarkMode(): boolean {
  if (typeof document === "undefined") return false;
  const root = document.documentElement;
  if (root.getAttribute("data-theme") === "dark") return true;
  if (root.classList.contains("dark")) return true;
  return false;
}

export function buildMermaidConfig(dark: boolean) {
  // mindmap は v11 で themeVariables を完全には反映しないので themeCSS で強制
  const sectionColors = [
    "#05B45B", // 0 (root) — primary green
    "#1D6FD3", // 1 — blue
    "#F15025", // 2 — orange
    "#7C3AED", // 3 — purple
    "#0EA5A4", // 4 — teal
    "#D97706", // 5 — amber
    "#BE185D", // 6 — pink
    "#475569", // 7 — slate
  ];
  const labelTextColor = "#FFFFFF";

  const mindmapTextCss = sectionColors
    .map(
      (bg, i) => `
    .mindmap-node.section-${i === 0 ? "root" : i - 1} > rect,
    .mindmap-node.section-${i === 0 ? "root" : i - 1} > circle,
    .mindmap-node.section-${i === 0 ? "root" : i - 1} > path {
      fill: ${bg} !important;
      stroke: ${bg} !important;
    }
    .mindmap-node.section-${i === 0 ? "root" : i - 1} text,
    .mindmap-node.section-${i === 0 ? "root" : i - 1} .nodeLabel,
    .mindmap-node.section-${i === 0 ? "root" : i - 1} foreignObject * {
      fill: ${labelTextColor} !important;
      color: ${labelTextColor} !important;
    }
    .edge.section-${i === 0 ? "root" : i - 1} {
      stroke: ${bg} !important;
    }
  `,
    )
    .join("\n");

  return {
    startOnLoad: false,
    theme: "base" as const,
    themeVariables: dark ? DARK_THEME_VARS : LIGHT_THEME_VARS,
    themeCSS: mindmapTextCss,
    securityLevel: "strict" as const,
    fontFamily: "Inter, 'Noto Sans JP', sans-serif",
    fontSize: 14,
    flowchart: { useMaxWidth: false, htmlLabels: true, curve: "basis" as const },
    sequence: { useMaxWidth: false, mirrorActors: false, actorMargin: 60 },
    er: { useMaxWidth: false, fontSize: 12 },
    gantt: { useMaxWidth: false, fontSize: 11 },
  };
}

let mermaidPromise: Promise<typeof import("mermaid").default> | null = null;
function loadMermaid(dark: boolean) {
  if (!mermaidPromise) {
    mermaidPromise = import("mermaid").then((mod) => {
      const mermaid = mod.default;
      mermaid.initialize(buildMermaidConfig(dark));
      return mermaid;
    });
  } else {
    // テーマが切り替わったら再初期化
    mermaidPromise = mermaidPromise.then((mermaid) => {
      mermaid.initialize(buildMermaidConfig(dark));
      return mermaid;
    });
  }
  return mermaidPromise;
}

interface MermaidBlockProps {
  code: string;
  onZoom?: (svgDataUrl: string) => void;
}

// SVG 挿入後の表示調整 (描画時もキャッシュからの復元時も同じ処理)
function applySvgPostProcessing(container: HTMLElement, dark: boolean): void {
  const svgEl = container.querySelector("svg");
  if (!svgEl) return;

  const viewBox = svgEl.getAttribute("viewBox");
  let intrinsicWidth: number | null = null;
  if (viewBox) {
    const parts = viewBox.split(/\s+/);
    if (parts.length === 4) intrinsicWidth = parseFloat(parts[2]);
  }
  if (intrinsicWidth == null) {
    const widthAttr = svgEl.getAttribute("width");
    if (widthAttr) intrinsicWidth = parseFloat(widthAttr);
  }
  svgEl.removeAttribute("style");
  svgEl.style.maxWidth = intrinsicWidth
    ? `${Math.min(intrinsicWidth, 900)}px`
    : "100%";
  svgEl.style.width = "100%";
  svgEl.style.height = "auto";
  svgEl.style.display = "block";
  svgEl.style.margin = "0 auto";

  const labelTextColor = dark ? "#D4D4D4" : "#2B2B2A";
  svgEl
    .querySelectorAll<HTMLElement>(
      ".edgeLabel, .edgeLabel *, .edgeLabel foreignObject *",
    )
    .forEach((el) => {
      el.style.color = labelTextColor;
    });
}

export function MermaidBlock({ code, onZoom }: MermaidBlockProps) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  const ref = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [svgMarkup, setSvgMarkup] = useState<string | null>(null);
  const [isDark, setIsDark] = useState<boolean>(() => detectDarkMode());
  // キャッシュヒットしていれば最初から true でレンダ済の SVG をそのまま挿す
  const cacheKey = hashKey(code, isDark);
  const initialCached = readCache(cacheKey);
  const [inView, setInView] = useState<boolean>(() => initialCached !== null);

  // data-theme / class="dark" の変化を監視して再描画
  useEffect(() => {
    if (typeof document === "undefined") return;
    const observer = new MutationObserver(() => {
      const next = detectDarkMode();
      // 値が実際に変わった時だけ state 更新（無限ループ防止）
      setIsDark((prev) => (prev === next ? prev : next));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "class"],
    });
    return () => observer.disconnect();
  }, []);

  // ビューポート進入を検知して遅延描画 (キャッシュヒット時はスキップ)
  useEffect(() => {
    if (inView) return;
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
            return;
          }
        }
      },
      { rootMargin: "300px 0px" }, // 画面外 300px から先読み
    );
    io.observe(node);
    return () => io.disconnect();
  }, [inView]);

  useEffect(() => {
    // effect はブラウザでしか走らない。SSR の束に mermaid を入れないための印
    if (import.meta.env.SSR) return;
    if (!inView) return;
    let cancelled = false;

    // === Cache hit ===: mermaid を読み込まず、保存済み SVG をそのまま反映
    const cached = readCache(cacheKey);
    if (cached) {
      setSvgMarkup(cached);
      setError(null);
      return;
    }

    loadMermaid(isDark)
      .then(async (mermaid) => {
        if (cancelled) return;
        try {
          const { svg } = await mermaid.render(`mermaid-${id}`, code);
          if (cancelled) return;
          writeCache(cacheKey, svg);
          setSvgMarkup(svg);
          setError(null);
        } catch (e) {
          if (!cancelled) {
            setError(e instanceof Error ? e.message : "Mermaid render failed");
          }
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Mermaid load failed");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [code, id, isDark, cacheKey, inView]);

  // svgMarkup が更新されたら post-processing を適用 (React の管理する DOM を直接いじらない)
  useEffect(() => {
    if (!svgMarkup || !ref.current) return;
    applySvgPostProcessing(ref.current, isDark);
  }, [svgMarkup, isDark]);

  if (error) {
    return (
      <div className="my-4 rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-xs font-bold text-red-800 mb-2">Mermaid render error</p>
        <pre className="text-xs text-red-700 whitespace-pre-wrap break-all">{error}</pre>
        <details className="mt-2">
          <summary className="text-xs text-red-600 cursor-pointer">Source</summary>
          <pre className="text-xs text-red-600 whitespace-pre-wrap break-all mt-1">
            {code}
          </pre>
        </details>
      </div>
    );
  }

  const handleZoom = () => {
    if (!onZoom || !svgMarkup) return;
    const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svgMarkup)}`;
    onZoom(dataUrl);
  };

  return (
    <div className="relative my-4">
      {svgMarkup ? (
        <div
          ref={ref}
          className="flex items-center justify-center overflow-auto rounded-lg border p-4"
          style={{
            maxHeight: "70vh",
            background: isDark ? "#1E1E1E" : "#F4F0EA",
            borderColor: isDark ? "#3A3A3A" : "#E0DACE",
          }}
          dangerouslySetInnerHTML={{ __html: svgMarkup }}
        />
      ) : (
        <div
          ref={ref}
          className="flex items-center justify-center overflow-auto rounded-lg border p-4"
          style={{
            maxHeight: "70vh",
            minHeight: 120,
            background: isDark ? "#1E1E1E" : "#F4F0EA",
            borderColor: isDark ? "#3A3A3A" : "#E0DACE",
          }}
        >
          <span
            className="text-xs font-mono select-none"
            style={{
              color: isDark ? "#A8A8A8" : "#5F5A52",
              letterSpacing: "0.02em",
            }}
          >
            {"図を読み込んでいます"}
          </span>
        </div>
      )}
      {onZoom && svgMarkup && (
        <button
          type="button"
          onClick={handleZoom}
          aria-label="図を拡大表示"
          className="absolute top-2 right-2 cursor-pointer rounded-md bg-white hover:bg-[var(--lg-card-hover,#f3f4f6)] border border-gray-300 px-2 py-1 text-xs font-bold text-gray-700"
        >
          拡大
        </button>
      )}
    </div>
  );
}
